import React, { useState, useEffect } from 'react';
import { X, Trash2, Save } from 'lucide-react';
import { KPIService } from '../../services/kpiService';
import { useTrackModal } from '../../hooks/useTrackModal';

/**
 * Modal for editing or deleting a correction log entry
 * Manager only
 */
const EditCorrectionModal = ({ correction, onClose, onSave, onDelete, userEmail }) => {
  useTrackModal("EditCorrectionModal");
  const [selectedReasons, setSelectedReasons] = useState(correction.selected_reasons || []);
  const [countsAgainstBuilder, setCountsAgainstBuilder] = useState(correction.counts_against_builder);
  const [comment, setComment] = useState(correction.comment || '');
  const [availableReasons, setAvailableReasons] = useState({ counts_against: [], does_not_count: [] });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    const loadReasons = async () => {
      try {
        const reasons = await KPIService.getCorrectionReasons();
        setAvailableReasons(reasons);
      } catch (err) {
        console.error('Error loading reasons:', err);
      }
    };
    loadReasons();
  }, []);

  const handleReasonToggle = (reason) => {
    const newReasons = selectedReasons.includes(reason)
      ? selectedReasons.filter(r => r !== reason)
      : [...selectedReasons, reason];

    setSelectedReasons(newReasons);

    // Auto-set counts_against_builder based on selected reasons
    // If ANY selected reason is in counts_against, it counts against builder
    const hasCountsAgainst = newReasons.some(r =>
      availableReasons.counts_against.includes(r)
    );
    setCountsAgainstBuilder(hasCountsAgainst);
  };

  const handleSave = async () => {
    if (selectedReasons.length === 0) {
      alert('Please select at least one reason');
      return;
    }

    setSaving(true);
    try {
      await KPIService.updateCorrection(correction.id, {
        selectedReasons,
        countsAgainstBuilder,
        comment
      }, userEmail);
      onSave();
    } catch (err) {
      console.error('Error saving correction:', err);
      alert('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await KPIService.deleteCorrection(correction.id, userEmail);
      onDelete();
    } catch (err) {
      console.error('Error deleting correction:', err);
      alert('Failed to delete: ' + err.message);
    } finally {
      setDeleting(false);
    }
  };

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 'var(--space-4)',
        overflow: 'auto'
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          maxWidth: '650px',
          width: '95%',
          maxHeight: '90vh',
          backgroundColor: 'var(--gray-900)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--gray-700)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: 'var(--space-4)',
          borderBottom: '1px solid var(--gray-700)'
        }}>
          <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 600, color: 'var(--gray-100)', margin: 0 }}>Edit Correction</h2>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--gray-400)',
              cursor: 'pointer',
              padding: 'var(--space-1)'
            }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: 'var(--space-4)', overflowY: 'auto', flex: 1 }}>
          {/* Project Info */}
          <div style={{ marginBottom: 'var(--space-4)', padding: 'var(--space-3)', background: 'var(--gray-800)', borderRadius: 'var(--radius-md)' }}>
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-400)' }}>Project</div>
            <div style={{ fontWeight: 500, color: 'var(--gray-100)' }}>
              {correction.job_number} - {correction.project_name}
            </div>
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-400)', marginTop: 'var(--space-1)' }}>
              Builder: {correction.modelBuilderName}
            </div>
          </div>

          {/* Reasons - Counts Against */}
          {availableReasons.counts_against.length > 0 && (
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--red-400)', marginBottom: 'var(--space-2)' }}>
                Counts Against Builder
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                {availableReasons.counts_against.map(reason => (
                  <button
                    key={reason}
                    type="button"
                    onClick={() => handleReasonToggle(reason)}
                    style={{
                      padding: 'var(--space-2) var(--space-3)',
                      fontSize: 'var(--text-sm)',
                      borderRadius: 'var(--radius-md)',
                      border: '2px solid',
                      borderColor: selectedReasons.includes(reason) ? 'var(--red-500)' : 'var(--gray-600)',
                      background: selectedReasons.includes(reason) ? 'var(--red-500)' : 'var(--gray-800)',
                      color: selectedReasons.includes(reason) ? 'white' : 'var(--gray-300)',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      fontWeight: selectedReasons.includes(reason) ? 600 : 400
                    }}
                  >
                    {selectedReasons.includes(reason) && '✓ '}{reason}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Reasons - Does Not Count */}
          {availableReasons.does_not_count.length > 0 && (
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--green-400)', marginBottom: 'var(--space-2)' }}>
                Does Not Count Against Builder
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                {availableReasons.does_not_count.map(reason => (
                  <button
                    key={reason}
                    type="button"
                    onClick={() => handleReasonToggle(reason)}
                    style={{
                      padding: 'var(--space-2) var(--space-3)',
                      fontSize: 'var(--text-sm)',
                      borderRadius: 'var(--radius-md)',
                      border: '2px solid',
                      borderColor: selectedReasons.includes(reason) ? 'var(--green-500)' : 'var(--gray-600)',
                      background: selectedReasons.includes(reason) ? 'var(--green-600)' : 'var(--gray-800)',
                      color: selectedReasons.includes(reason) ? 'white' : 'var(--gray-300)',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      fontWeight: selectedReasons.includes(reason) ? 600 : 400
                    }}
                  >
                    {selectedReasons.includes(reason) && '✓ '}{reason}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Comment */}
          <div style={{ marginBottom: 'var(--space-4)' }}>
            <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--gray-300)', marginBottom: 'var(--space-2)' }}>
              Comment
            </label>
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              rows={6}
              style={{
                width: '100%',
                padding: 'var(--space-3)',
                fontSize: 'var(--text-sm)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--gray-600)',
                background: 'var(--gray-800)',
                color: 'var(--gray-100)',
                resize: 'vertical',
                minHeight: '120px',
                fontFamily: 'inherit',
                lineHeight: 1.5
              }}
            />
          </div>

          {/* KPI Impact (computed from selected reasons) */}
          {selectedReasons.length > 0 && (
            <div style={{
              fontSize: 'var(--text-xs)',
              color: countsAgainstBuilder ? 'var(--red-400)' : 'var(--green-400)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-1)'
            }}>
              {countsAgainstBuilder ? '⚠' : '✓'}
              <span>
                {countsAgainstBuilder
                  ? 'Counts against builder (red reason selected)'
                  : 'Does not count against builder'}
              </span>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', padding: 'var(--space-4)', borderTop: '1px solid var(--gray-700)', backgroundColor: 'var(--gray-850, var(--gray-800))' }}>
          {/* Delete Button */}
          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={deleting}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                padding: 'var(--space-2) var(--space-3)',
                fontSize: 'var(--text-sm)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--red-500)',
                background: 'transparent',
                color: 'var(--red-500)',
                cursor: 'pointer'
              }}
            >
              <Trash2 size={16} />
              Delete
            </button>
          ) : (
            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
              <button
                onClick={handleDelete}
                disabled={deleting}
                style={{
                  padding: 'var(--space-2) var(--space-3)',
                  fontSize: 'var(--text-sm)',
                  borderRadius: 'var(--radius-md)',
                  border: 'none',
                  background: 'var(--red-500)',
                  color: 'white',
                  cursor: deleting ? 'not-allowed' : 'pointer'
                }}
              >
                {deleting ? 'Deleting...' : 'Confirm Delete'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  padding: 'var(--space-2) var(--space-3)',
                  fontSize: 'var(--text-sm)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--gray-600)',
                  background: 'transparent',
                  color: 'var(--gray-300)',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          )}

          {/* Save/Cancel */}
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <button
              onClick={onClose}
              style={{
                padding: 'var(--space-2) var(--space-3)',
                fontSize: 'var(--text-sm)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--gray-600)',
                background: 'transparent',
                color: 'var(--gray-300)',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || selectedReasons.length === 0}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                padding: 'var(--space-2) var(--space-3)',
                fontSize: 'var(--text-sm)',
                borderRadius: 'var(--radius-md)',
                border: 'none',
                background: 'var(--primary-500)',
                color: 'white',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: selectedReasons.length === 0 ? 0.5 : 1
              }}
            >
              <Save size={16} />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditCorrectionModal;
